// Certificados del cliente como insumo del proyecto NIW.
//
// POR QUÉ ESTÁ EN EL PASO DE GENERACIÓN Y NO SÓLO EN LA FICHA
//   Es una OPCIÓN de cómo hacer el proyecto, no un dato administrativo del
//   cliente. El operador decide acá, mirando el proyecto que va a generar, si
//   este caso se ancla a certificados o no. Ponerlo sólo en la ficha lo deja
//   fuera de la vista en el momento en que importa.
//
// LOS DOCUMENTOS SE GUARDAN EN EL CLIENTE, NO EN EL NIW
//   El certificado laboral vale igual para el proyecto, el whitepaper y las
//   cartas. Cargarlo una vez por entregable son seis oportunidades de que una
//   copia quede vieja. Por eso el endpoint es el del dossier.
//
// QUÉ PROBLEMA RESUELVE
//   Una clienta reportó (oct 2026) que su proyecto daba bien el período de
//   DISAMA MEDIC en una sección y le asignaba el de otra institución en cuatro
//   más. Las secciones se generan en grupos separados y nada anclaba el dato:
//   cada grupo lo infería por su cuenta.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Loader2, Paperclip, Trash2, Upload } from 'lucide-react';
import { subirAlDossier, errorDeSubida } from '../utils/subirAlDossier';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// Mismas claves que `CLASES` en documentos_respaldo.py. Si se agrega una allá,
// agregarla acá: una clase que el backend no conoce cae en "otro" y el
// documento se guarda igual, pero el bloque lo agrupa mal.
const CLASES = [
  ['certificado_laboral', 'Certificado laboral', 'Institución, cargo y período'],
  ['titulo_academico', 'Título académico', 'Diploma o certificado de registro'],
  ['certificado_servicio', 'Certificado de servicio', 'Servicio comunitario, horas'],
  ['evidencia_proyecto', 'Evidencia de proyecto', 'Resolución, acta, captura, publicación'],
  ['otro', 'Otro', ''],
];

const ETIQUETA = Object.fromEntries(CLASES.map(([k, l]) => [k, l]));

export default function DocumentosRespaldo({ clientId, token }) {
  const inputRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [clase, setClase] = useState('certificado_laboral');
  const [nota, setNota] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [cargando, setCargando] = useState(false);

  const auth = useCallback(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    [token]);

  const cargar = useCallback(async () => {
    if (!clientId) return;
    setCargando(true);
    try {
      const r = await axios.get(`${API}/clients/${clientId}/dossier`, auth());
      setDocs(r.data?.supporting_docs || []);
    } catch (e) {
      // Silencioso: es un panel opcional. Si falla, el operador genera sin
      // certificados igual que antes — romperle la pantalla de generación por
      // un listado sería peor que no mostrarlo.
      setDocs([]);
    } finally {
      setCargando(false);
    }
  }, [clientId, auth]);

  useEffect(() => { cargar(); }, [cargar]);

  // EN SERIE, UNO POR UNO. No es lentitud mal resuelta: guardar un certificado
  // es leer el dossier del cliente, agregarle el documento y reescribirlo
  // entero. Dos subidas en paralelo leen la misma version y la segunda pisa a
  // la primera — las dos responden 200 y un certificado desaparece sin dejar
  // rastro. Con varios documentos del mismo cliente eso no es improbable, es lo
  // normal.
  const subirVarios = async (files) => {
    const lista = Array.from(files || []);
    if (!lista.length) return;
    setSubiendo(true);
    let ok = 0;
    const fallaron = [];
    for (const file of lista) {
      try {
        const fd = new FormData();
        fd.append('kind', 'supporting');
        fd.append('file', file);
        fd.append('clase', clase);
        fd.append('nota', nota);
        const res = await subirAlDossier(clientId, fd, auth());
        if (res) ok += 1;
        else fallaron.push(`${file.name}: no se guardó (es de otro cliente)`);
      } catch (e) {
        // Uno que falla no frena a los demas: se reporta al final y el
        // operador reintenta ese solo.
        fallaron.push(`${file.name}: ${errorDeSubida(e, 'no se pudo leer')}`);
      }
    }
    if (ok) toast.success(`${ok} documento${ok === 1 ? '' : 's'} cargado${ok === 1 ? '' : 's'}`);
    if (fallaron.length) toast.error(fallaron.slice(0, 3).join(' · '));
    setNota('');
    await cargar();
    setSubiendo(false);
  };

  const borrar = async (doc) => {
    try {
      await axios.delete(
        `${API}/clients/${clientId}/dossier/supporting/${doc.id}`, auth());
      await cargar();
    } catch (e) {
      toast.error('No pude quitarlo.');
    }
  };

  if (!clientId) {
    return (
      <div style={S.aviso}>
        <Paperclip size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          Para anclar el proyecto a los certificados del cliente, entrá desde su
          ficha. Los certificados se guardan en el cliente y los reusan los
          demás entregables.
        </span>
      </div>
    );
  }

  return (
    <div style={S.caja}>
      <div style={S.titulo}>
        <Paperclip size={16} /> Documentos de respaldo
        {docs.length > 0 && <span style={S.contador}>{docs.length}</span>}
      </div>
      <p style={S.ayuda}>
        Opcional. Podés adjuntar varios a la vez. Mandan sobre el CV en fechas,
        períodos, instituciones, cargos y horas — en <strong>todas</strong> las
        secciones del proyecto, y Mónica también los lee para proponer el
        proyecto.
      </p>

      {docs.length > 0 && (
        <div style={S.lista}>
          {docs.map((d) => (
            <div key={d.id} style={S.fila}>
              <FileText size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.nombre}>{d.filename || 'documento'}</div>
                <div style={S.meta}>
                  {ETIQUETA[d.clase] || 'Otro'}
                  {d.nota ? ` · ${d.nota}` : ''}
                  {/* Quién y cuándo: hubo documentos que nadie reconocía haber subido. */}
                  {d.added_at ? ` · ${new Date(d.added_at).toLocaleDateString('es')}` : ''}
                  {d.added_by ? ` · subido por ${d.added_by}` : ''}
                </div>
              </div>
              <button type="button" onClick={() => borrar(d)}
                      style={S.borrar} title="Quitar">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={S.form}>
        <select value={clase} onChange={(e) => setClase(e.target.value)}
                style={S.select} disabled={subiendo}>
          {CLASES.map(([k, l, d]) => (
            <option key={k} value={k}>{l}{d ? ` — ${d}` : ''}</option>
          ))}
        </select>

        {/* La nota es lo que el documento NO dice y la persona que conoce el
            caso sí. Nació de un reclamo concreto: 60 horas de servicio
            comunitario certificado que el proyecto describió como "aporte". */}
        <input
          type="text" value={nota} maxLength={600}
          onChange={(e) => setNota(e.target.value)} disabled={subiendo}
          placeholder="Aclaración (opcional): ej. &quot;son 60 horas de servicio comunitario certificado, no un aporte&quot;"
          style={S.input}
        />

        <button type="button" onClick={() => inputRef.current?.click()}
                disabled={subiendo} style={S.boton}>
          {subiendo ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {subiendo ? 'Leyendo…' : 'Adjuntar documentos'}
        </button>
        <input ref={inputRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.md"
               style={{ display: 'none' }}
               onChange={(e) => { subirVarios(e.target.files); e.target.value = ''; }} />
      </div>

      {cargando && <div style={S.meta}>Cargando…</div>}
    </div>
  );
}

const S = {
  caja: {
    marginTop: '1rem', padding: '1rem', border: '1px solid #e5e7eb',
    borderRadius: 8, background: '#fff', color: '#111827',
  },
  titulo: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontWeight: 600, fontSize: '0.95rem', color: '#111827',
  },
  contador: {
    background: '#111827', color: '#fff', borderRadius: 10,
    padding: '1px 8px', fontSize: '0.72rem',
  },
  ayuda: { fontSize: '0.82rem', color: '#4b5563', margin: '6px 0 10px' },
  lista: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 },
  fila: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
    background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6,
  },
  nombre: {
    fontSize: '0.85rem', color: '#111827', overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  meta: { fontSize: '0.75rem', color: '#6b7280' },
  borrar: {
    background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer',
    padding: 4, flexShrink: 0,
  },
  form: { display: 'flex', flexDirection: 'column', gap: 8 },
  select: {
    padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: '0.85rem', color: '#111827', background: '#fff',
  },
  input: {
    padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: '0.85rem', color: '#111827',
  },
  boton: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 6,
    background: '#fff', color: '#111827', fontSize: '0.87rem', cursor: 'pointer',
  },
  aviso: {
    display: 'flex', gap: 8, marginTop: '1rem', padding: '0.85rem',
    border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 8,
    fontSize: '0.82rem', color: '#92400e',
  },
};

export { DocumentosRespaldo };
